import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentFailureBanner = ({ paymentFailures, onUpdatePayment }) => {
  if (!paymentFailures || paymentFailures?.length === 0) return null;

  const latestFailure = paymentFailures?.[0];
  const failureCount = latestFailure?.retry_count || 1;
  const amount = latestFailure?.amount || 0;
  const nextRetry = latestFailure?.next_retry_at;

  const getUrgencyLevel = () => {
    if (failureCount >= 3) return 'critical';
    if (failureCount >= 2) return 'high';
    return 'medium';
  };

  const urgency = getUrgencyLevel();

  const urgencyStyles = {
    medium: {
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/20',
      icon: 'text-orange-500',
      button: 'warning'
    },
    high: {
      bg: 'bg-red-500/10',
      border: 'border-red-500/20',
      icon: 'text-red-500',
      button: 'danger'
    },
    critical: {
      bg: 'bg-red-500/20',
      border: 'border-red-500/30',
      icon: 'text-red-500',
      button: 'danger'
    }
  };

  const styles = urgencyStyles?.[urgency];

  const getMessage = () => {
    if (failureCount >= 3) {
      return {
        title: '⚠️ Critical: Payment Failed Multiple Times',
        description: `We've been unable to process your payment of $${amount?.toFixed(2)} after ${failureCount} attempts. Your subscription is at risk of cancellation. Please update your payment method immediately to avoid service interruption.`
      };
    }
    if (failureCount >= 2) {
      return {
        title: '⚠️ Urgent: Second Payment Attempt Failed',
        description: `Your payment of $${amount?.toFixed(2)} has failed twice. We'll retry ${nextRetry ? `on ${new Date(nextRetry)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'soon'}. Please update your payment method to avoid subscription suspension.`
      };
    }
    return {
      title: 'Payment Failed - Action Required',
      description: `We were unable to process your payment of $${amount?.toFixed(2)}. We'll automatically retry ${nextRetry ? `on ${new Date(nextRetry)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'soon'}. Please update your payment method to ensure uninterrupted service.`
    };
  };

  const message = getMessage();

  return (
    <div className={`${styles?.bg} border ${styles?.border} rounded-xl p-6`}>
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <Icon name="AlertTriangle" size={24} className={styles?.icon} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground font-heading mb-1">
              {message?.title}
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              {message?.description}
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Icon name="CreditCard" size={14} />
                <span>Amount: ${amount?.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Icon name="RefreshCw" size={14} />
                <span>Attempt {failureCount} of 3</span>
              </div>
              {nextRetry && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Icon name="Calendar" size={14} />
                  <span>Next retry: {new Date(nextRetry)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
          <Button
            variant={styles?.button}
            size="md"
            onClick={onUpdatePayment}
            iconName="CreditCard"
            iconPosition="left"
            className="w-full sm:w-auto"
          >
            Update Payment Method
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailureBanner;